class CurveMarqueeScroll extends HTMLElement {
  static get observedAttributes() {
    return ['scroll-speed','direction', 'disabled'];
  }

  constructor() {
    super();
    this._ticking = false;
    this._loopPx = 0;
    this._centerPx = 0;

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._updateFromScroll = this._updateFromScroll.bind(this);
  }

  connectedCallback() {
    this._path = this.querySelector('[data-curve-path]');
    this._tp = this.querySelector('[data-curve-textpath]');
    this._pattern = this.querySelector('[data-curve-pattern]');

    if (!this._path || !this._tp || !this._pattern) return;
    if (this.hasAttribute('disabled')) return;

    // Reduced motion
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Init after fonts, for stability
    const init = () => {
      this._measure();
      this._updateFromScroll();
      this._bind();
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) init();
      });
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.isConnected) init();
      }));
    }
  }

  disconnectedCallback() {
    this._unbind();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;

    if (name === 'disabled') {
      if (this.hasAttribute('disabled')) this._unbind();
      else if (this.isConnected) {
        this._measure();
        this._updateFromScroll();
        this._bind();
      }
    }

    if (name === 'scroll-speed') {
      // No special work needed; next scroll/raf uses new speed.
      this._updateFromScroll();
    }
  }

  _bind() {
    if (this._bound) return;
    this._bound = true;

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  _unbind() {
    if (!this._bound) return;
    this._bound = false;

    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  }

  _measure() {
    // pattern is typically a <text> element; getComputedTextLength exists on SVGTextContentElement
    const measureEl = this._pattern || this._tp;

    this._loopPx = 0;
    if (measureEl && typeof measureEl.getComputedTextLength === 'function') {
      try {
        this._loopPx = measureEl.getComputedTextLength();
      } catch {}
    }

    // Fallback for Safari: use getBBox width
    if (!this._loopPx && measureEl && typeof measureEl.getBBox === 'function') {
      try {
        this._loopPx = measureEl.getBBox().width;
      } catch {}
    }

    try {
      this._centerPx = this._path.getTotalLength() / 2;
    } catch {
      this._centerPx = 0;
    }
  }

  _wrapNegative(val, wrapPoint) {
    // returns value in (-wrapPoint .. 0]
    val = val % wrapPoint;
    if (val > 0) val -= wrapPoint;
    return val;
  }

  _getScrollSpeed() {
    const v = Number(this.getAttribute('scroll-speed'));
    return Number.isFinite(v) ? v : 1.25;
  }

  _updateFromScroll() {
    this._ticking = false;
    if (!this._loopPx || !this._tp) return;

    const scrollSpeed = this._getScrollSpeed();
    const x = (window.scrollY * scrollSpeed);

    const offset = this._wrapNegative(x, this._loopPx);
    this._tp.setAttribute('startOffset', `${this._centerPx + offset}px`);
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(this._updateFromScroll);
  }

  _onResize() {
    this._measure();
    this._updateFromScroll();
  }
}

if (!customElements.get('curve-marquee-scroll')) {
  customElements.define('curve-marquee-scroll', CurveMarqueeScroll);
}
